import { ConflictChange } from './orderPickingConstants';
import type { PickingFilterChip } from './orderPickingConstants';

/** Normalized order line after API map (mobile mapItemDetails). */
export function mapItemDetails(item: any): any {
  const mapped = { ...item };
  mapped.disabled = false;
  mapped.pickedAll = false;
  const pickedQty = item.picked_quantity;
  mapped.pickedQty = pickedQty;
  mapped.confirmed = item.done_picked_qty;
  const codes = item.code_quantites || item.code_quantities || [];
  mapped.codePickedQtys = codes.map((c: any) => ({
    ...c,
    pickedQty: c.picked_quantity,
  }));
  return mapped;
}

export interface CommodityGroupTreeNode {
  id: string;
  name: string;
  children: CommodityGroupTreeNode[];
  hasProducts?: boolean;
}

export function initCGTree(
  tree: CommodityGroupTreeNode[],
  products: any[],
): CommodityGroupTreeNode[] {
  return tree.map((t) => {
    const found = products.find((p) => p.product?.commodity_group?.uuid === t.id);
    const next: CommodityGroupTreeNode = {
      ...t,
      hasProducts: found !== undefined,
      children: initCGTree(t.children || [], products),
    };
    return next;
  });
}

export function initConflictItems(
  data: { comparison_data: any[] },
  orderItems: any[],
  existingItems: any[],
  changeConfirmed = true,
): { conflicted: any[]; editedItems: any[] } {
  const cItems: any[] = [];
  let nextEdited = existingItems;

  for (const d of data.comparison_data || []) {
    for (const reason of d.reasons || []) {
      const r = reason.reason;
      const old_value = reason.old_value;
      const new_value = reason.new_value;
      if (r === ConflictChange.ITEM_DELETED) {
        for (const ei of nextEdited) {
          if (ei.id === d.item.id) {
            ei.has_changed = true;
            ei.old_value = ei.quantity;
            ei.new_value = '0';
            ei.is_deleted = true;
            ei.change_reason = r;
            cItems.push(ei);
          }
        }
      }
      if (r === ConflictChange.EXPECTED_QTY_CHANGE) {
        for (const ei of nextEdited) {
          if (ei.id === d.item.id) {
            ei.has_changed = true;
            ei.old_value = old_value;
            ei.new_value = new_value;
            ei.quantity = new_value;
            ei.change_reason = r;
            if (changeConfirmed) {
              ei.confirmed = false;
              ei.pickedAll = false;
            }
            cItems.push(ei);
          }
        }
      }
      if (r === ConflictChange.ITEM_ADDED) {
        for (const ei of orderItems) {
          if (ei.id === d.item.id) {
            ei.has_changed = true;
            ei.is_new = true;
            ei.change_reason = r;
            const fi = mapItemDetails(ei);
            cItems.push(fi);
            nextEdited = [...nextEdited, fi];
          }
        }
      }
    }
  }
  return { conflicted: cItems, editedItems: nextEdited };
}

export function recalcPickedFromCodes(item: any): void {
  const totalQty = item.codePickedQtys
    .map((c: any) => Number(c.pickedQty))
    .reduce((a: number, b: number) => a + b, 0);
  item.pickedQty = String(totalQty);
}

export function incrementPickedQtyByCase(
  number: number,
  item: any,
  cpq: any,
  _cpqIndex: number,
  originalQty: number,
): void {
  let convertedQty = Number(cpq.pickedQty);
  convertedQty += number * cpq.case_quantity;
  cpq.pickedQty = String(Math.max(0, convertedQty));
  recalcPickedFromCodes(item);
  const totalQty = Number(item.pickedQty);
  if (totalQty === item.quantity) {
    item.pickedAll = true;
    item.confirmed = true;
  } else {
    item.pickedAll = false;
    item.confirmed = false;
  }
}

export function decrementPickedQtyByCase(
  number: number,
  item: any,
  cpq: any,
  _cpqIndex: number,
  originalOrderedQuantity: number,
): void {
  let convertedQty = Number(cpq.pickedQty);
  convertedQty -= number * cpq.case_quantity;
  cpq.pickedQty = String(Math.max(0, convertedQty));
  recalcPickedFromCodes(item);
  const totalQty = Number(item.pickedQty);
  if (totalQty === originalOrderedQuantity) {
    item.pickedAll = true;
    item.confirmed = true;
  } else {
    item.pickedAll = false;
    item.confirmed = false;
  }
}

/** Direct edit of one code row — mobile OrderDetailCardItem onReceiveQtyTextChange. */
export function onReceiveQtyTextChange(
  value: string | number,
  item: any,
  cpq: any,
  _cpqIndex: number,
): void {
  cpq.pickedQty = String(value);
  recalcPickedFromCodes(item);
}

export function applyPickAll(item: any, turnOn: boolean): void {
  item.pickedAll = turnOn;
  const allQuantity = item.quantity;
  if (turnOn) {
    item.confirmed = true;
    if (item.codePickedQtys.length === 1) {
      item.codePickedQtys[0].pickedQty = String(allQuantity);
    }
    item.pickedQty = String(allQuantity);
  } else {
    item.confirmed = false;
  }
}

export function getConstraintKeysForItem(item: any): string[] {
  const constraint: string[] = [];
  const picked_qty =
    item.pickedQty && item.pickedQty !== '' ? Number(item.pickedQty) : NaN;

  if (picked_qty === 0) {
    constraint.push('pickedQty=zero');
  }
  if (String(item.pickedQty || '').trim() === '') {
    constraint.push('pickedQty=empty');
  }
  if (!Number.isNaN(picked_qty)) {
    if (picked_qty < item.original_ordered_quantity) {
      constraint.push('short=true');
    }
    if (picked_qty === item.original_ordered_quantity) {
      constraint.push('match=true');
    }
    if (picked_qty > item.original_ordered_quantity) {
      constraint.push('over=true');
    }
  }
  return constraint;
}

export function applyDoneFilter(
  list: any[],
  filtersSet1: PickingFilterChip[],
): any[] {
  let result: any[] = [];
  if (filtersSet1[0]?.name === 'Yes' && filtersSet1[0].isSelected) {
    result = [...result, ...list.filter((i) => i.confirmed)];
  }
  if (filtersSet1[1]?.name === 'No' && filtersSet1[1].isSelected) {
    result = [...result, ...list.filter((i) => !i.confirmed)];
  }
  return result;
}

export function applyPickQtyFilter(
  list: any[],
  filtersSet2: PickingFilterChip[],
): any[] {
  const applied: Record<string, boolean> = {};
  filtersSet2.forEach((i) => {
    if (i.isSelected) {
      applied[i.constraint] = true;
    }
  });
  return list.filter((item) => {
    const constraintList = getConstraintKeysForItem(item);
    return constraintList.some((f) => applied[f]);
  });
}

export function applySearchProduct(list: any[], searchTerm: string): any[] {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) {
    return list;
  }
  return list.filter((i: any) => {
    if (!i.product) {
      return false;
    }
    if (i.product.name && i.product.name.toLowerCase().includes(term)) {
      return true;
    }
    if (i.product.suppliers && i.product.suppliers.length > 0) {
      return i.product.suppliers.some((c: any) =>
        String(c.orderNumber || '').includes(term),
      );
    }
    if (i.product.article_codes && i.product.article_codes.length > 0) {
      return i.product.article_codes.some((c: any) =>
        String(c.code || '').includes(term),
      );
    }
    return false;
  });
}

export type ApplyPickingFiltersInput = {
  editedItems: any[] | null;
  filtersSet1: PickingFilterChip[];
  filtersSet2: PickingFilterChip[];
  searchTerm: string;
};

/** Returns null when no filter should narrow the list (use full editedItems + orig tree). */
export function applyPickingFiltersResult(input: ApplyPickingFiltersInput): any[] | null {
  const { editedItems, filtersSet1, filtersSet2, searchTerm } = input;
  if (!editedItems || editedItems.length === 0) {
    return null;
  }
  let resultant = [...editedItems];
  const isDoneFilterApplied = filtersSet1.some((item) => item.isSelected);
  const isPickQtyFilterApplied = filtersSet2.some((item) => item.isSelected);
  const isSearchApplied = Boolean(searchTerm && searchTerm !== '');

  if (isDoneFilterApplied) {
    resultant = applyDoneFilter(resultant, filtersSet1);
  }
  if (isPickQtyFilterApplied) {
    resultant = applyPickQtyFilter(resultant, filtersSet2);
  }
  if (isSearchApplied) {
    resultant = applySearchProduct(resultant, searchTerm);
  }

  if (!isDoneFilterApplied && !isPickQtyFilterApplied && !isSearchApplied) {
    return null;
  }
  return resultant;
}

/** Merge filtered edits back into full edited list (mobile changeExistingData). */
export function mergeFilteredIntoEdited(
  filteredItems: any[] | null,
  editedItems: any[] | null,
): any[] | null {
  if (!editedItems) {
    return null;
  }
  if (filteredItems && filteredItems.length > 0) {
    const filteredProducts = filteredItems.map((i: any) =>
      JSON.parse(JSON.stringify(i)),
    );
    const editedProducts = editedItems.map((i: any) =>
      JSON.parse(JSON.stringify(i)),
    );
    return editedProducts.map((x) => {
      const item = filteredProducts.find(
        (f: any) => f.id === x.id || f.product === x.product,
      );
      return item || x;
    });
  }
  return editedItems.map((i: any) => JSON.parse(JSON.stringify(i)));
}

export function getStatusTextForQty(item: any): string {
  const convertedPickedQty = Number(item.pickedQty);
  const qty = Number(item.quantity);
  if (convertedPickedQty > qty) {
    return 'Over';
  }
  if (convertedPickedQty === qty) {
    return 'Ok';
  }
  return 'Short';
}
